import type * as React from "react";
import type { DefaultLegendContentProps } from "recharts";
import { Legend } from "recharts";

import {
  type ChartConfig,
  getPayloadConfigFromPayload,
  resolveChartColorFromConfig,
  resolveChartConfigDatumKey,
  useChart,
} from "@/components/ui/ChartTooltip";
import { cn } from "@/lib/utils";

const ChartLegend = Legend;

const legendSwatchStyle = (swatchColor: string | undefined) =>
  swatchColor != null && swatchColor !== ""
    ? { backgroundColor: swatchColor, borderColor: swatchColor }
    : undefined;

const ChartLegendItemRow = ({
  hideIcon = false,
  icon: Icon,
  label,
  swatchColor,
}: {
  hideIcon?: boolean;
  icon?: React.ComponentType;
  label?: React.ReactNode;
  swatchColor?: string;
}) => (
  <div className="flex items-center gap-1 text-xxs [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-foreground dark:[&>svg]:text-gray-300">
    {Icon && !hideIcon ? (
      <Icon />
    ) : (
      <div
        className="h-1.5 w-1.5 shrink-0 rounded-[2px] border border-solid"
        style={legendSwatchStyle(swatchColor)}
      />
    )}
    {label}
  </div>
);

const ChartLegendItems = ({
  className,
  verticalAlign = "bottom",
  children,
}: React.ComponentProps<"div"> & {
  verticalAlign?: DefaultLegendContentProps["verticalAlign"];
}) => (
  <div
    className={cn(
      "flex items-center justify-center gap-3 leading-snug text-foreground dark:text-gray-300",
      verticalAlign === "top" ? "pb-3" : "pt-3",
      className,
    )}
  >
    {children}
  </div>
);

const ChartLegendContent = ({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey,
}: React.ComponentProps<"div"> & {
  hideIcon?: boolean;
  nameKey?: string;
} & Pick<DefaultLegendContentProps, "payload" | "verticalAlign">) => {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <ChartLegendItems className={className} verticalAlign={verticalAlign}>
      {payload
        .filter((item) => item.type !== "none")
        .map((item, index) => {
          const datumKey = resolveChartConfigDatumKey(item, nameKey);
          const itemConfig = getPayloadConfigFromPayload(
            config,
            item,
            datumKey,
          );
          const swatchColor =
            item.color ??
            resolveChartColorFromConfig(itemConfig, datumKey) ??
            (item.payload as { fill?: string } | undefined)?.fill;

          return (
            <ChartLegendItemRow
              // biome-ignore lint/suspicious/noArrayIndexKey: legend order matches Recharts payload
              key={index}
              hideIcon={hideIcon}
              icon={itemConfig?.icon}
              label={itemConfig?.label}
              swatchColor={swatchColor}
            />
          );
        })}
    </ChartLegendItems>
  );
};

/** Legend rows from series keys + config; for use outside Recharts `<Legend>`. */
const ChartLegendSeriesContent = ({
  className,
  config,
  hideIcon = false,
  series,
  verticalAlign = "bottom",
}: React.ComponentProps<"div"> & {
  config: ChartConfig;
  hideIcon?: boolean;
  series: { key: string; label?: string }[];
  verticalAlign?: DefaultLegendContentProps["verticalAlign"];
}) => {
  if (series.length === 0) {
    return null;
  }

  return (
    <ChartLegendItems className={className} verticalAlign={verticalAlign}>
      {series.map((item) => {
        const itemConfig = config[item.key];
        const swatchColor =
          typeof itemConfig?.color === "string"
            ? itemConfig.color
            : resolveChartColorFromConfig(itemConfig, item.key);

        return (
          <ChartLegendItemRow
            key={item.key}
            hideIcon={hideIcon}
            icon={itemConfig?.icon}
            label={itemConfig?.label ?? item.label ?? item.key}
            swatchColor={swatchColor}
          />
        );
      })}
    </ChartLegendItems>
  );
};

export { ChartLegend, ChartLegendContent, ChartLegendSeriesContent };
